import { Button, DataTable, Modal } from 'shared/components/ui';
import React, { useState, useCallback } from 'react';
import { useEntradasMensuales } from '../hooks/useEntradasMensuales';
import { CENTROS } from '../utils/constantesGastos';
import { exportEntradas } from '../utils/excelGastosEntradas';
import FormEntrada from './FormEntrada';
import './EntradasMensuales.css';

const formatMonto = (valor) => {
  const num = Number(valor) || 0;
  return num.toLocaleString('es-AR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatFecha = (fecha) => {
  if (!fecha) return '';
  const parts = fecha.split('-');
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return fecha;
};

const EntradasMensuales = ({ mes, anio, onToast }) => {
  const {
    entradas,
    totalMensual,
    sumaAcumulada,
    centroSeleccionado,
    setCentroSeleccionado,
    loading,
    saving,
    error,
    buscar,
    crear,
    actualizar,
    eliminar,
  } = useEntradasMensuales({ onToast });

  const [buscado, setBuscado] = useState(false);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [entradaEditar, setEntradaEditar] = useState(null);
  const [confirmEliminar, setConfirmEliminar] = useState(null);

  const handleBuscar = useCallback(() => {
    setBuscado(true);
    buscar(mes, anio, centroSeleccionado);
  }, [buscar, mes, anio, centroSeleccionado]);

  const handleCentroChange = (e) => {
    setCentroSeleccionado(Number(e.target.value));
  };

  const handleNuevaEntrada = () => {
    setEntradaEditar(null);
    setMostrarForm(true);
  };

  const handleEditar = (entrada) => {
    setEntradaEditar(entrada);
    setMostrarForm(true);
  };

  const handleCancelForm = () => {
    setMostrarForm(false);
    setEntradaEditar(null);
  };

  const handleSubmitForm = async (data) => {
    const payload = {
      ...data,
      idCentro: centroSeleccionado,
      mes,
      anio,
    };

    let resultado;
    if (entradaEditar) {
      resultado = await actualizar(entradaEditar.id, payload);
    } else {
      resultado = await crear(payload);
    }

    if (resultado && resultado.valid) {
      setMostrarForm(false);
      setEntradaEditar(null);
    }

    return resultado;
  };

  const handleEliminarClick = (entrada) => {
    setConfirmEliminar(entrada);
  };

  const handleConfirmarEliminar = async () => {
    if (confirmEliminar) {
      await eliminar(confirmEliminar.id);
      setConfirmEliminar(null);
    }
  };

  const handleCancelarEliminar = () => {
    setConfirmEliminar(null);
  };

  const handleExportar = useCallback(() => {
    try {
      const centroObj = CENTROS.find((c) => c.id === centroSeleccionado);
      const centroLabel = centroObj ? centroObj.label : '';
      exportEntradas(entradas, totalMensual, sumaAcumulada, centroLabel, mes, anio);
    } catch (err) {
      console.error('Error al exportar entradas:', err);
      if (onToast) {
        onToast({ type: 'error', message: 'Error al generar el archivo de exportación' });
      }
    }
  }, [entradas, totalMensual, sumaAcumulada, centroSeleccionado, mes, anio, onToast]);

  const tieneData = entradas.length > 0;

  return (
    <div className="entradas-container" role="region" aria-label="Entradas mensuales por centro">
      {/* Toolbar */}
      <div className="entradas-toolbar">
        <div className="entradas-filtros">
          <label className="entradas-label" htmlFor="entradas-centro">
            Centro
          </label>
          <select
            id="entradas-centro"
            className="entradas-select"
            value={centroSeleccionado}
            onChange={handleCentroChange}
            disabled={loading || saving}
            aria-label="Seleccionar centro"
          >
            {CENTROS.map((centro) => (
              <option key={centro.id} value={centro.id}>
                {centro.label}
              </option>
            ))}
          </select>
        </div>

        <div className="entradas-acciones-toolbar">
          <Button
            className="btn-buscar"
            onClick={handleBuscar}
            disabled={loading}
            aria-label="Buscar entradas del período y centro seleccionados"
          >
            {loading ? 'Buscando...' : 'Buscar'}
          </Button>
          <Button
            className="btn-nueva-entrada"
            onClick={handleNuevaEntrada}
            disabled={loading || saving}
            aria-label="Nueva entrada"
          >
            Nueva Entrada
          </Button>
          <Button
            className="btn-exportar"
            onClick={handleExportar}
            disabled={loading || !tieneData}
            aria-label="Exportar entradas a Excel"
          >
            Exportar Excel
          </Button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="entradas-error" role="alert" aria-live="assertive">
          {error}
        </div>
      )}

      {/* Formulario */}
      {mostrarForm && (
        <div className="entradas-form-wrapper">
          <h3 className="entradas-form-titulo">
            {entradaEditar ? 'Editar Entrada' : 'Nueva Entrada'}
          </h3>
          <FormEntrada
            entrada={entradaEditar}
            onSubmit={handleSubmitForm}
            onCancel={handleCancelForm}
            loading={saving}
            mesActual={mes}
            anioActual={anio}
          />
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="entradas-loading" role="status" aria-live="polite">
          <div className="entradas-spinner" aria-hidden="true"></div>
          <span>Cargando entradas...</span>
        </div>
      )}

      {/* Tabla */}
      {!loading && buscado && tieneData && (
        <>
          <div className="entradas-tabla-wrap" role="region" aria-label="Tabla de entradas mensuales">
            <DataTable className="entradas-tabla" aria-label="Entradas mensuales">
              <thead>
                <tr>
                  <th scope="col" className="entradas-th">Fecha</th>
                  <th scope="col" className="entradas-th">Descripción</th>
                  <th scope="col" className="entradas-th entradas-th-monto">Monto</th>
                  <th scope="col" className="entradas-th entradas-th-acciones">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {entradas.map((entrada) => (
                  <tr key={entrada.id} className="entradas-fila">
                    <td className="entradas-celda-fecha">{formatFecha(entrada.fecha)}</td>
                    <td className="entradas-celda-descripcion">{entrada.descripcion}</td>
                    <td className="entradas-celda-monto">${formatMonto(entrada.monto)}</td>
                    <td className="entradas-celda-acciones">
                      <Button
                        className="btn-accion btn-editar"
                        onClick={() => handleEditar(entrada)}
                        disabled={saving}
                        aria-label={`Editar entrada: ${entrada.descripcion}`}
                      >
                        Editar
                      </Button>
                      <Button
                        className="btn-accion btn-eliminar"
                        onClick={() => handleEliminarClick(entrada)}
                        disabled={saving}
                        aria-label={`Eliminar entrada: ${entrada.descripcion}`}
                      >
                        Eliminar
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          </div>

          {/* Totales */}
          <div className="entradas-totales" aria-label="Totales de entradas">
            <div className="entradas-total-item">
              <span className="entradas-total-label">Total mensual:</span>
              <span className="entradas-total-valor">${formatMonto(totalMensual)}</span>
            </div>
            <div className="entradas-total-item">
              <span className="entradas-total-label">Suma acumulada:</span>
              <span className="entradas-total-valor entradas-acumulada">${formatMonto(sumaAcumulada)}</span>
            </div>
          </div>
        </>
      )}

      {/* Estado vacío */}
      {!loading && buscado && !tieneData && (
        <div className="entradas-empty" role="status" aria-live="polite">
          <p>No se encontraron entradas para el período y centro seleccionados.</p>
          <p>Presioná "Nueva Entrada" para agregar una.</p>
        </div>
      )}

      {/* Estado inicial (antes de buscar) */}
      {!loading && !buscado && (
        <div className="entradas-empty" role="status">
          <p>Seleccioná un centro y período, luego presioná "Buscar" para cargar las entradas.</p>
        </div>
      )}

      {/* Diálogo de confirmación de eliminación */}
      {confirmEliminar && (
        <Modal show onHide={saving ? undefined : handleCancelarEliminar} backdrop={saving ? 'static' : true} keyboard={!saving} size="sm">
          <Modal.Header>
            <Modal.Title>
              ¿Eliminar esta entrada?
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="entradas-dialog-descripcion">
              Se eliminará la entrada "{confirmEliminar.descripcion}" por ${formatMonto(confirmEliminar.monto)}.
            </p>
          </Modal.Body>
            <Modal.Footer>
              <Button
                className="btn-accion btn-confirmar"
                variant="danger"
                onClick={handleConfirmarEliminar}
                disabled={saving}
                aria-label="Confirmar eliminación"
              >
                Confirmar
              </Button>
              <Button
                className="btn-accion btn-cancelar"
                variant="secondary"
                onClick={handleCancelarEliminar}
                disabled={saving}
                aria-label="Cancelar eliminación"
              >
                Cancelar
              </Button>
            </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default EntradasMensuales;
